<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
	import { Button } from '$lib/components/ui/button';
	import { Card, CardContent, CardHeader, CardTitle } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import { page } from '$app/stores';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	const seo = createPageSEO({
		title: 'How Our AI Matching Works - TalentProfile',
		description:
			"Learn how TalentProfile's AI-powered matching algorithm connects developers with the perfect job opportunities.",
		canonical: '/how-it-works',
		ogImage: '/how-it-works.png',
		structuredData: createWebPageSchema({
			name: 'How It Works',
			description: "Understanding TalentProfile's AI-powered job matching process",
			url: '/how-it-works'
		})
	});
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white to-gray-50">
	<!-- Hero Section -->
	<section class="container mx-auto px-4 py-20">
		<div class="mx-auto max-w-4xl text-center">
			<Badge class="mb-4" variant="secondary">Behind the Scenes</Badge>

			<h1 class="mb-6 text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
				How Our AI Matching
				<span class="text-blue-600"> Actually Works</span>
			</h1>

			<p class="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600">
				We combine traditional algorithms with AI to ensure you only see jobs that truly match your
				skills and experience.
			</p>
		</div>
	</section>

	<!-- The Matching Process -->
	<section class="container mx-auto px-4 py-16">
		<div class="mx-auto max-w-5xl">
			<h2 class="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				The Three-Layer Matching System
			</h2>

			<div class="space-y-16">
				<!-- Layer 1: Role Classification -->
				<div class="grid items-center gap-8 md:grid-cols-2">
					<div>
						<div
							class="superellipse mb-4 inline-flex h-12 w-12 items-center justify-center bg-blue-100 text-xl font-bold text-blue-600 transition-transform hover:scale-110"
						>
							1
						</div>
						<h3 class="mb-4 text-2xl font-bold text-gray-900">Role Classification</h3>
						<p class="mb-4 text-gray-600">
							Before we even look at skills, we classify both developers and jobs into role
							categories:
						</p>
						<ul class="space-y-2 text-gray-600">
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>Software Engineers (Backend, Frontend, Full Stack)</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>QA/Testing Engineers</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>Data Engineers & Scientists</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>DevOps & SRE</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>Mobile Developers</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-green-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>Product & Design</span>
							</li>
						</ul>
						<div class="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
							<p class="text-sm text-amber-900">
								<strong>Why this matters:</strong> We'll never match a QA Engineer to a Software Developer
								role, even if they both know Python.
							</p>
						</div>
					</div>
					<div class="rounded-lg bg-gray-100 p-8">
						<div
							class="mb-4 rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
						>
							<div class="mb-2 flex items-center justify-between">
								<span class="text-sm font-medium text-gray-700">Your Profile</span>
								<Badge variant="outline">Senior QA Engineer</Badge>
							</div>
							<div class="text-2xl font-bold text-blue-600">→ QA/Testing</div>
						</div>
						<div class="flex items-center justify-center py-4">
							<svg
								class="h-8 w-8 text-gray-400"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<div
							class="rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
						>
							<div class="mb-2 flex items-center justify-between">
								<span class="text-sm font-medium text-gray-700">Job Posting</span>
								<Badge variant="outline">QA Automation Lead</Badge>
							</div>
							<div class="text-2xl font-bold text-green-600">→ QA/Testing ✓</div>
						</div>
					</div>
				</div>

				<!-- Layer 2: Skills Matching -->
				<div class="grid items-center gap-8 md:grid-cols-2">
					<div class="order-2 rounded-lg bg-gray-100 p-8 md:order-1">
						<div class="space-y-3">
							<div
								class="rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
							>
								<div class="flex items-center justify-between">
									<span class="font-medium">Python</span>
									<Badge class="bg-green-100 text-green-800">Match</Badge>
								</div>
							</div>
							<div
								class="rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
							>
								<div class="flex items-center justify-between">
									<span class="font-medium">Selenium</span>
									<Badge class="bg-green-100 text-green-800">Match</Badge>
								</div>
							</div>
							<div
								class="rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
							>
								<div class="flex items-center justify-between">
									<span class="font-medium">CI/CD</span>
									<Badge class="bg-green-100 text-green-800">Match</Badge>
								</div>
							</div>
							<div class="rounded-lg bg-white p-4 opacity-50 shadow-sm">
								<div class="flex items-center justify-between">
									<span class="font-medium text-gray-500">professional</span>
									<Badge variant="outline" class="text-gray-400">Ignored</Badge>
								</div>
							</div>
							<div class="rounded-lg bg-white p-4 opacity-50 shadow-sm">
								<div class="flex items-center justify-between">
									<span class="font-medium text-gray-500">team</span>
									<Badge variant="outline" class="text-gray-400">Ignored</Badge>
								</div>
							</div>
						</div>
						<div class="mt-6 text-center">
							<div class="text-3xl font-bold text-blue-600">85% Match</div>
							<p class="mt-1 text-sm text-gray-600">3 of 3 technical skills matched</p>
						</div>
					</div>
					<div class="order-1 md:order-2">
						<div
							class="superellipse mb-4 inline-flex h-12 w-12 items-center justify-center bg-blue-100 text-xl font-bold text-blue-600 transition-transform hover:scale-110"
						>
							2
						</div>
						<h3 class="mb-4 text-2xl font-bold text-gray-900">Smart Skills Matching</h3>
						<p class="mb-4 text-gray-600">After role filtering, we analyze technical skills:</p>
						<ul class="space-y-3 text-gray-600">
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-blue-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
									/>
								</svg>
								<span><strong>Extract real technical skills</strong> from your CV and GitHub</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-blue-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									/>
								</svg>
								<span
									><strong>Filter out generic terms</strong> like "professional", "team player", "with"</span
								>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-blue-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
								<span
									><strong>Fuzzy matching</strong> to handle variations (React.js = ReactJS = React)</span
								>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-blue-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 10V3L4 14h7v7l9-11h-7z"
									/>
								</svg>
								<span
									><strong>Synonym expansion</strong> (JavaScript matches Node.js, TypeScript)</span
								>
							</li>
						</ul>
					</div>
				</div>

				<!-- Layer 3: AI Verification -->
				<div class="grid items-center gap-8 md:grid-cols-2">
					<div>
						<div
							class="superellipse mb-4 inline-flex h-12 w-12 items-center justify-center bg-blue-100 text-xl font-bold text-blue-600 transition-transform hover:scale-110"
						>
							3
						</div>
						<h3 class="mb-4 text-2xl font-bold text-gray-900">AI Quality Check</h3>
						<p class="mb-4 text-gray-600">
							Every match is verified by Claude AI before we send it to you:
						</p>
						<ul class="space-y-3 text-gray-600">
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-purple-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
									/>
								</svg>
								<span>Reviews your full profile vs. the job description</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-purple-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<span>Checks experience level requirements (Junior vs Senior)</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-purple-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
								<span>Flags potential mismatches (e.g., remote vs. on-site)</span>
							</li>
							<li class="flex items-start">
								<svg
									class="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-purple-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
									/>
								</svg>
								<span>Scores each match 0-100 for final quality assurance</span>
							</li>
						</ul>
						<div class="mt-6 rounded-lg border border-purple-200 bg-purple-50 p-4">
							<p class="text-sm text-purple-900">
								<strong>Quality threshold:</strong> We only send matches with 75%+ AI confidence score.
							</p>
						</div>
					</div>
					<div class="rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 p-8">
						<div
							class="group rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
						>
							<div class="mb-4 flex items-center gap-3">
								<div
									class="superellipse flex h-10 w-10 items-center justify-center bg-purple-600 transition-transform group-hover:scale-110"
								>
									<svg
										class="h-6 w-6 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
										/>
									</svg>
								</div>
								<div>
									<div class="font-semibold text-gray-900">Claude AI Review</div>
									<div class="text-xs text-gray-500">Analyzing match quality...</div>
								</div>
							</div>
							<div class="space-y-2 text-sm">
								<div class="flex justify-between">
									<span class="text-gray-600">Role Match</span>
									<span class="font-semibold text-green-600">✓ Perfect</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-600">Skills Overlap</span>
									<span class="font-semibold text-green-600">85%</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-600">Experience Level</span>
									<span class="font-semibold text-green-600">✓ Match</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-600">Location</span>
									<span class="font-semibold text-green-600">✓ Remote OK</span>
								</div>
							</div>
							<div class="mt-6 border-t pt-4">
								<div class="text-center">
									<div class="mb-1 text-3xl font-bold text-green-600">92/100</div>
									<div class="text-xs text-gray-600">Confidence Score</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mx-auto mt-24 max-w-4xl">
			<GlopayCtaHorizontal audience="talent" placement="how-it-works-post-matching-process" />
		</div>
	</section>

	<!-- Key Stats -->
	<section class="bg-blue-600 py-16">
		<div class="container mx-auto px-4">
			<div class="mx-auto grid max-w-4xl grid-cols-1 gap-8 text-center text-white md:grid-cols-3">
				<div>
					<div class="mb-2 text-4xl font-bold">75%</div>
					<div class="text-blue-100">Minimum Match Threshold</div>
				</div>
				<div>
					<div class="mb-2 text-4xl font-bold">3-Layer</div>
					<div class="text-blue-100">Quality Verification</div>
				</div>
				<div>
					<div class="mb-2 text-4xl font-bold">30K+</div>
					<div class="text-blue-100">Jobs Scanned Monthly</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Why It Matters -->
	<section class="container mx-auto px-4 py-20">
		<div class="mx-auto max-w-4xl">
			<h2 class="mb-8 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Why This Approach Works
			</h2>

			<div class="grid gap-6 md:grid-cols-2">
				<Card class="shadow-none">
					<CardHeader>
						<CardTitle class="flex items-center gap-3">
							<div class="superellipse flex h-10 w-10 items-center justify-center bg-red-100">
								<svg
									class="h-5 w-5 text-red-600"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</div>
							<span>Traditional Job Boards</span>
						</CardTitle>
					</CardHeader>
					<CardContent class="space-y-2 text-sm text-gray-600">
						<p>❌ Keyword matching only</p>
						<p>❌ QA Engineer matched to Software Developer roles</p>
						<p>❌ "Professional" and "team" count as skills</p>
						<p>❌ No quality verification</p>
						<p>❌ Spam you with 100+ irrelevant jobs</p>
					</CardContent>
				</Card>

				<Card class="border-2 border-green-200">
					<CardHeader>
						<CardTitle class="flex items-center gap-3">
							<div class="superellipse flex h-10 w-10 items-center justify-center bg-green-100">
								<svg
									class="h-5 w-5 text-green-600"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<span>TalentProfile</span>
						</CardTitle>
					</CardHeader>
					<CardContent class="space-y-2 text-sm text-gray-600">
						<p>✅ Role-aware matching first</p>
						<p>✅ QA only sees QA roles</p>
						<p>✅ Generic terms automatically filtered</p>
						<p>✅ AI verifies every match</p>
						<p>✅ 3-5 highly relevant jobs only</p>
					</CardContent>
				</Card>
			</div>
		</div>
	</section>

	<!-- Contact Frequency -->
	<section class="bg-gray-50 py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-3xl text-center">
				<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">We Respect Your Inbox</h2>
				<p class="mb-8 text-lg text-gray-600">
					We'll never contact you more than once per month unless you ask for weekly updates.
				</p>
				<div class="rounded-lg bg-white p-8 shadow-sm">
					<div class="grid grid-cols-2 gap-6 text-left">
						<div>
							<div class="mb-2 font-semibold text-gray-900">For Developers</div>
							<ul class="space-y-1 text-sm text-gray-600">
								<li>• Choose weekly or monthly digests</li>
								<li>• Only 3-5 top matches per email</li>
								<li>• Unsubscribe anytime</li>
								<li>• Update preferences anytime</li>
							</ul>
						</div>
						<div>
							<div class="mb-2 font-semibold text-gray-900">For Companies</div>
							<ul class="space-y-1 text-sm text-gray-600">
								<li>• Instant notifications for new matches</li>
								<li>• Daily digest of all applicants</li>
								<li>• No spam, only qualified candidates</li>
								<li>• Control notification settings</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- CTA -->
	<section class="container mx-auto px-4 py-20">
		<div
			class="mx-auto max-w-3xl rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white"
		>
			<h2 class="mb-4 text-3xl font-bold md:text-4xl">Experience Smart Matching</h2>
			<p class="mb-8 text-lg opacity-90">
				See for yourself why thousands trust our AI-powered matching
			</p>
			<div class="flex flex-col items-center justify-center gap-4 sm:flex-row">
				<Button size="lg" variant="secondary" class="px-8 text-lg" href="/">Upload Your CV</Button>
				<Button
					size="lg"
					variant="outline"
					class="border-white bg-transparent px-8 text-lg text-white hover:bg-white hover:text-blue-600"
					href="/hire"
				>
					Post a Job
				</Button>
			</div>
			<p class="mt-6 text-sm opacity-75">100% free • No credit card required</p>
		</div>
	</section>
</div>
